import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Share, Plus, Smartphone } from "lucide-react";

/**
 * Botón "Instalar app".
 *  - Android Chrome: usa el evento beforeinstallprompt (instalación nativa).
 *  - iOS Safari: muestra instrucciones manuales (Compartir → "Agregar a inicio").
 *  - Si la app ya está instalada (display-mode standalone), no se muestra.
 */
export default function InstallPWAButton({ className = "" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) setInstalled(true);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);

  const handleClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try { await deferredPrompt.userChoice; } catch (_) {}
      setDeferredPrompt(null);
      return;
    }
    if (isIos) { setShowIosHelp(true); return; }
    setShowIosHelp(true);
  };

  if (!deferredPrompt && !isIos) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        className={`gap-2 ${className}`}
      >
        <Download className="h-4 w-4" />
        Instalar app
      </Button>

      <Dialog open={showIosHelp} onOpenChange={setShowIosHelp}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-[#0FB4B7]" />
              Instalar Odontofix
            </DialogTitle>
          </DialogHeader>
          {isIos && isSafari ? (
            <div className="space-y-3 text-sm text-gray-700">
              <p>Para instalar Odontofix en tu iPhone o iPad:</p>
              <ol className="space-y-2 list-decimal list-inside">
                <li className="flex items-center gap-2">
                  <span>Toca el ícono</span>
                  <Share className="h-4 w-4 text-blue-600" />
                  <span>(Compartir) en la barra inferior.</span>
                </li>
                <li className="flex items-center gap-2">
                  <span>Elige</span>
                  <Plus className="h-4 w-4" />
                  <span>"Agregar a pantalla de inicio".</span>
                </li>
                <li>Confirma con "Agregar".</li>
              </ol>
            </div>
          ) : isIos ? (
            <p className="text-sm text-gray-700">
              Abre este sitio en <strong>Safari</strong> para poder instalar la app
              (otros navegadores en iOS no permiten instalar PWAs).
            </p>
          ) : (
            <p className="text-sm text-gray-700">
              Tu navegador no soporta instalación directa. En Android usa Chrome;
              en iPhone/iPad usa Safari.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
